import { css, Global } from '@emotion/react';
import {
    createContext,
    RefObject,
    useCallback,
    useContext,
    useRef,
} from 'react';

import { Provider } from 'react-redux';
import {
    CHAT_BLK_TOGGLE_EVENT,
    CHAT_BLK_TOGGLE_EVENT_CLOSE,
    CHAT_BLK_TOGGLE_EVENT_OPEN,
} from '../constants';
import { useInitServices } from '../hooks';
import {
    AuthProvider,
    GlobalEventListenerProvider,
    ImageModalProvider,
    NotificationProvider,
} from '../providers';
import { ChatBlkBaseService, ChatBlkTelemetryService } from '../services';
import store from '../store/store';
import {
    ChatBlkAppContext,
    ChatBLKToggleEvent,
    telemetryFunctionMap,
} from '../types';

import '../styles.scss';
import { getCacheValue } from '../utils';

const chatBlkWrapperStyles = css`
    .chat-blk__wrapper {
        height: 100%;
        width: 100%;
        position: fixed;
        bottom: 0;
        z-index: 5;
        background: transparent;
        pointer-events: none;
    }
`;

export const ChatBlkContext = createContext<{
    isEmbedded?: boolean;
    chatCanvasRef?: RefObject<HTMLBxfChatCanvasElement>;
    promptBoxRef?: RefObject<HTMLBxfChatPromptBoxElement>;
    open?: () => void;
    close?: () => void;
}>({ isEmbedded: true });

export const useChatBlkContext = () => useContext(ChatBlkContext);

export interface ChatBlkProviderProps {
    service?: ChatBlkBaseService;
    children?: JSX.Element;
    appContext?: ChatBlkAppContext;
}
const ChatBlkProvider = ({
    children,
    appContext,
    service,
}: ChatBlkProviderProps) => {
    const { isEmbedded, serviceInstance } = useInitServices(
        service,
        appContext
    );
    const chatCanvasRef: RefObject<HTMLBxfChatCanvasElement> =
        useRef<HTMLBxfChatCanvasElement>(null);
    const promptBoxRef: RefObject<HTMLBxfChatPromptBoxElement> =
        useRef<HTMLBxfChatPromptBoxElement>(null);

    /**
     * Callback function that dispatches a custom event to open the chat mfe.
     *
     * <p>
     * Retrieves cached values for 'relatedQuestion' and 'summarize', constructs
     * a custom event with the details including the action type, and dispatches it
     * on the global window object. Additionally, this function triggers a telemetry
     * tracking event to indicate that the chat block was opened from the header.
     * </p>
     *
     * @remarks
     * Should only use this function to open the chat mfe so that host applications
     * can easily track when the chat mfe is opened from the header.
     *
     */
    const open = useCallback(() => {
        const relatedQuestion = getCacheValue('relatedQuestion');
        const summarize = Boolean(getCacheValue('summarize'));
        const action = CHAT_BLK_TOGGLE_EVENT_OPEN;
        const event = new CustomEvent<ChatBLKToggleEvent>(
            CHAT_BLK_TOGGLE_EVENT,
            {
                detail: {
                    action,
                    relatedQuestion: relatedQuestion as string,
                    relatedQuestionId: getCacheValue(
                        'relatedQuestionId'
                    ) as string,
                    summarize,
                },
            }
        );
        window.dispatchEvent(event);
        ChatBlkTelemetryService?.track(
            telemetryFunctionMap.CHATBLK_MFE_FUNCTION_OPENED_FROM_HEADER
        );
    }, []);

    /**
     * Callback function that dispatches a custom event to open the chat mfe.
     *
     * <p>
     * Retrieves cached values for 'relatedQuestion' and 'summarize', constructs
     * a custom event with the details including the action type, and dispatches it
     * on the global window object. Additionally, this function triggers a telemetry
     * tracking event to indicate that the chat mfe was opened from the header.
     * </p>
     *
     * @remarks
     * Should only use this function to open the chat mfe so that host applications
     * can easily track when the chat mfe is opened from the header.
     *
     */
    const close = useCallback(() => {
        const relatedQuestion = getCacheValue('relatedQuestion');
        const summarize = Boolean(getCacheValue('summarize'));
        const action = CHAT_BLK_TOGGLE_EVENT_CLOSE;
        const event = new CustomEvent<ChatBLKToggleEvent>(
            CHAT_BLK_TOGGLE_EVENT,
            {
                detail: {
                    action,
                    relatedQuestion: relatedQuestion as string,
                    relatedQuestionId: getCacheValue(
                        'relatedQuestionId'
                    ) as string,
                    summarize,
                },
            }
        );
        window.dispatchEvent(event);
        ChatBlkTelemetryService?.track(
            telemetryFunctionMap.CHATBLK_MFE_FUNCTION_CLOSED
        );
    }, []);

    if (serviceInstance?.appContext) {
        return (
            <ChatBlkContext.Provider
                value={{ isEmbedded, chatCanvasRef, promptBoxRef, open, close }}
            >
                <Provider store={store}>
                    <AuthProvider>
                        <GlobalEventListenerProvider>
                            <ImageModalProvider>
                                <NotificationProvider>
                                    <>
                                        <Global styles={chatBlkWrapperStyles} />
                                        {children}
                                    </>
                                </NotificationProvider>
                            </ImageModalProvider>
                        </GlobalEventListenerProvider>
                    </AuthProvider>
                </Provider>
            </ChatBlkContext.Provider>
        );
    }
};

ChatBlkProvider.displayName = 'ChatBlkProvider';

export default ChatBlkProvider;
